import {
  FeatureGating,
  IntroductoryOfferEligibility,
  OnDeviceCache,
  PaywallDismissal,
  PaywallPresentationStyle,
  PresentationCondition,
  ShowPaywallOption,
} from "@superwall/db/constants"

export const showPaywallOptions: {
  value: ShowPaywallOption
  label: string
}[] = [
  { value: "EXPLICIT", label: "When triggered programatically" },
  { value: "ON_FIRST_LAUNCH", label: "On first app launch" },
  { value: "ON_NTH_LAUNCH", label: "On second and future app launch" },
]

export const presentationStyleOptions: {
  value: PaywallPresentationStyle
  label: string
}[] = [
  { value: "MODAL", label: "Modal" },
  { value: "FULLSCREEN", label: "Full Screen" },
  { value: "PUSH", label: "Push (requires v2.4.0)" },
  { value: "NO_ANIMATION", label: "No animation (requires v2.4.0)" },
  { value: "DRAWER", label: "Drawer (requires v3.x.x)" },
]

export const presentationConditionOptions: {
  value: PresentationCondition
  label: string
}[] = [
  { value: "CHECK_USER_SUBSCRIPTION", label: "If not subscribed (recommended)" },
  { value: "ALWAYS", label: "Always" },
]

export const featureGatingOptions: {
  value: FeatureGating
  label: string
}[] = [
  { value: "GATED", label: "Gated" },
  { value: "NON_GATED", label: "Non Gated" },
]

export const onDeviceCacheOptions: {
  value: OnDeviceCache
  label: string
}[] = [
  { value: "ENABLED", label: "Enabled" },
  { value: "DISABLED", label: "Disabled" },
]

export const introductoryOfferEligibilityOptions: {
  value: IntroductoryOfferEligibility
  label: string
}[] = [
  { value: "AUTOMATIC", label: "Automatic" },
  { value: "ALWAYS_ELIGIBLE", label: "Always Eligible" },
  { value: "ALWAYS_INELIGIBLE", label: "Always Ineligible" },
]

export const dismissalOptions: {
  value: PaywallDismissal
  label: string
}[] = [
  { value: "NORMAL", label: "Normal - Allow users to dismiss paywall" },
  { value: "DISABLED", label: "Disabled - Prevent users from dismissing paywalls" },
]

export const productsAutofill = ["primary", "secondary", "tertiary"].flatMap((toplvl) => {
  return [
    { label: `${toplvl}.currencyCode`, type: "variable", info: "example: USD" },
    { label: `${toplvl}.currencySymbol`, type: "variable", info: "example: $" },
    { label: `${toplvl}.dailyPrice`, type: "variable", info: "example: $0.25" },
    { label: `${toplvl}.languageCode`, type: "variable", info: "example: en" },
    { label: `${toplvl}.locale`, type: "variable", info: "example: en_US@currency=USD" },
    { label: `${toplvl}.monthlyPrice`, type: "variable", info: "example: $7.50" },
    { label: `${toplvl}.period`, type: "variable", info: "example: year" },
    { label: `${toplvl}.periodAlt`, type: "variable", info: "example: 1 yr" },
    { label: `${toplvl}.periodDays`, type: "variable", info: "example: 365" },
    { label: `${toplvl}.periodly`, type: "variable", info: "example: yearly" },
    { label: `${toplvl}.periodMonths`, type: "variable", info: "example: 12" },
    { label: `${toplvl}.periodWeeks`, type: "variable", info: "example: 52" },
    { label: `${toplvl}.periodYears`, type: "variable", info: "example: 1" },
    { label: `${toplvl}.price`, type: "variable", info: "example: $89.99" },
    { label: `${toplvl}.rawPrice`, type: "variable", info: "example: 89.99" },
    { label: `${toplvl}.trialPeriodPrice`, type: "variable", info: "example: $0.99" },
    { label: `${toplvl}.rawTrialPeriodPrice`, type: "variable", info: "example: 0.99" },
    { label: `${toplvl}.trialPeriodDailyPrice`, type: "variable", info: "example: $0.14" },
    { label: `${toplvl}.trialPeriodWeeklyPrice`, type: "variable", info: "example: $0.99" },
    { label: `${toplvl}.trialPeriodMonthlyPrice`, type: "variable", info: "example: $4.29" },
    { label: `${toplvl}.trialPeriodYearlyPrice`, type: "variable", info: "example: $51.58" },
    { label: `${toplvl}.trialPeriodDays`, type: "variable", info: "example: 7" },
    { label: `${toplvl}.trialPeriodMonths`, type: "variable", info: "example: 0" },
    { label: `${toplvl}.trialPeriodText`, type: "variable", info: "example: 7-day" },
    { label: `${toplvl}.trialPeriodWeeks`, type: "variable", info: "example: 1" },
    { label: `${toplvl}.trialPeriodYears`, type: "variable", info: "example: 0" },
    { label: `${toplvl}.weeklyPrice`, type: "variable", info: "example: $1.73" },
    { label: `${toplvl}.yearlyPrice`, type: "variable", info: "example: $89.99" },
  ]
})

/**
 * This is the first phase of how we'll keep track of all the product variables
 * for the new autocomplete feature. For this phase we'll simply keep a flat map
 * of all possible variables. The new phase is forecasted in the commented-out
 * code below the following productsVariables const.
 */
export type TOption = {
  label: string
  // String literal of the object path
  template: string
  // Exact match of the template string, if found
  value?: string
  // Hard-coded example value if the above value is not found
  example: string
  // Optional badge to display next to the label (e.g. "SDK >= 3.1.0")
  badge?: string | null
}

export const productsVariables: TOption[] = ["primary", "secondary", "tertiary"].flatMap(
  (toplvl) => {
    return [
      { label: "Currency Code", template: `${toplvl}.currencyCode`, example: "USD" },
      { label: "Currency Symbol", template: `${toplvl}.currencySymbol`, example: "$" },
      { label: "Daily Price", template: `${toplvl}.dailyPrice`, example: "$0.25" },
      { label: "Language Code", template: `${toplvl}.languageCode`, example: "en" },
      { label: "Locale", template: `${toplvl}.locale`, example: "en_US@currency=USD" },
      { label: "Monthly Price", template: `${toplvl}.monthlyPrice`, example: "$7.50" },
      { label: "Period", template: `${toplvl}.period`, example: "year" },
      { label: "Period Alt", template: `${toplvl}.periodAlt`, example: "1 yr" },
      { label: "Period Days", template: `${toplvl}.periodDays`, example: "365" },
      { label: "Periodly", template: `${toplvl}.periodly`, example: "yearly" },
      { label: "Period Months", template: `${toplvl}.periodMonths`, example: "12" },
      { label: "Period Weeks", template: `${toplvl}.periodWeeks`, example: "52" },
      { label: "Period Years", template: `${toplvl}.periodYears`, example: "1" },
      { label: "Price", template: `${toplvl}.price`, example: "" },
      { label: "Raw Price", template: `${toplvl}.rawPrice`, example: "" },
      { label: "Trial Period Price", template: `${toplvl}.trialPeriodPrice`, example: "$0.99" },
      {
        label: "Raw Trial Period Price",
        template: `${toplvl}.rawTrialPeriodPrice`,
        example: "0.99",
      },
      {
        label: "Trial Period Daily Price",
        template: `${toplvl}.trialPeriodDailyPrice`,
        example: "$0.14",
      },
      {
        label: "Trial Period Weekly Price",
        template: `${toplvl}.trialPeriodWeeklyPrice`,
        example: "$0.99",
      },
      {
        label: "Trial Period Monthly Price",
        template: `${toplvl}.trialPeriodMonthlyPrice`,
        example: "$4.29",
      },
      {
        label: "Trial Period Yearly Price",
        template: `${toplvl}.trialPeriodYearlyPrice`,
        example: "$51.58",
      },
      { label: "Trial Period Days", template: `${toplvl}.trialPeriodDays`, example: "7" },
      { label: "Trial Period Months", template: `${toplvl}.trialPeriodMonths`, example: "0" },
      { label: "Trial Period Text", template: `${toplvl}.trialPeriodText`, example: "7-day" },
      { label: "Trial Period Weeks", template: `${toplvl}.trialPeriodWeeks`, example: "1" },
      { label: "Trial Period Years", template: `${toplvl}.trialPeriodYears`, example: "0" },
      { label: "Weekly Price", template: `${toplvl}.weeklyPrice`, example: "$1.73" },
      { label: "Yearly Price", template: `${toplvl}.yearlyPrice`, example: "" },
    ]
  }
)

/**
 * Use the below commented-out code when we start supporting nested product variable groups.
 * Until then use the above flat map of all the possible product variables.
 */
// export type TOption = {
//   label: string
//   template: string
//   value: string
// }

// export type TProductVariables = {
//   label: string
//   options: TOption[]
// }

// export const productsVariables: TProductVariables[] = ["primary", "secondary", "tertiary"].map(
//   (toplvl) => {
//     return {
//       label: `${_.capitalize(toplvl)} Product`,
//       options: [
//         { label: "Currency Code", template: `${toplvl}.currencyCode`, value: "USD" },
//         { label: "Currency Symbol", template: `${toplvl}.currencySymbol`, value: "$" },
//         { label: "Daily Price", template: `${toplvl}.dailyPrice`, value: "$0.25" },
//         { label: "Language Code", template: `${toplvl}.languageCode`, value: "en" },
//         { label: "Locale", template: `${toplvl}.locale`, value: "en_US@currency=USD" },
//         { label: "Monthly Price", template: `${toplvl}.monthlyPrice`, value: "$7.50" },
//         { label: "Period", template: `${toplvl}.period`, value: "year" },
//         { label: "Period Alt", template: `${toplvl}.periodAlt`, value: "1 yr" },
//         { label: "Period Days", template: `${toplvl}.periodDays`, value: "365" },
//         { label: "Periodly", template: `${toplvl}.periodly`, value: "yearly" },
//         { label: "Period Months", template: `${toplvl}.periodMonths`, value: "12" },
//         { label: "Period Weeks", template: `${toplvl}.periodWeeks`, value: "52" },
//         { label: "Period Years", template: `${toplvl}.periodYears`, value: "1" },
//         { label: "Price", template: `${toplvl}.price`, value: "$89.99" },
//         { label: "Raw Price", template: `${toplvl}.rawPrice`, value: "89.99" },
//         { label: "Trial Period Days", template: `${toplvl}.trialPeriodDays`, value: "7" },
//         { label: "Trial Period Months", template: `${toplvl}.trialPeriodMonths`, value: "0" },
//         { label: "Trial Period Text", template: `${toplvl}.trialPeriodText`, value: "7-day" },
//         { label: "Trial Period Weeks", template: `${toplvl}.trialPeriodWeeks`, value: "1" },
//         { label: "Trial Period Years", template: `${toplvl}.trialPeriodYears`, value: "0" },
//         { label: "Weekly Price", template: `${toplvl}.weeklyPrice`, value: "$1.73" },
//         { label: "Yearly Price", template: `${toplvl}.yearlyPrice`, value: "$89.99" },
//       ],
//     }
//   }
// )
